import { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function InstallBanner() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if already dismissed or already installed
    const dismissed = sessionStorage.getItem('pwa_banner_dismissed');
    if (dismissed) return;

    // Check if running as standalone (already installed)
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Listen for the browser's install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Fallback for iOS (no beforeinstallprompt support)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    if (isIOS && isSafari) {
      setTimeout(() => setShow(true), 3000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShow(false);
      }
      setDeferredPrompt(null);
    } else {
      // iOS instructions
      setShow(false);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    sessionStorage.setItem('pwa_banner_dismissed', '1');
  };

  if (!show) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up"
      data-testid="install-banner"
    >
      <div className="mx-3 mb-3 bg-[#1F3F72] rounded-xl shadow-2xl shadow-black/30 p-4 border border-white/10">
        <div className="flex items-start gap-3">
          {/* App Icon */}
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
            <Download className="w-6 h-6 text-[#F25C05]" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-sm">
              Installer DestockPro
            </p>
            {isIOS ? (
              <p className="text-blue-200 text-xs mt-0.5 leading-relaxed">
                Appuyez sur <span className="inline-block mx-0.5">
                  <svg className="w-3.5 h-3.5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12" /></svg>
                </span> puis "Sur l'ecran d'accueil"
              </p>
            ) : (
              <p className="text-blue-200 text-xs mt-0.5">
                Acces rapide depuis votre ecran d'accueil
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {!isIOS && (
              <Button
                size="sm"
                className="bg-[#F25C05] hover:bg-[#d94f04] text-white text-xs h-8 px-3 rounded-lg"
                onClick={handleInstall}
                data-testid="install-btn"
              >
                Installer
              </Button>
            )}
            <button
              onClick={handleDismiss}
              className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="install-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
